import type { User } from '../../types/auth';
import {
  ReportStatus,
  ReportType,
  ReportTypeCategory,
  type ReportTypeIdentifier,
} from '../../types/reports';

/**
 * Rechtefunktion Rolle x Meldungsart x Status fuer Liste und Meldungsseite.
 *
 * Spiegel der Backend-Matrix `ReportVoter` (D-20, Korrektur 2026-09-30): Pruefer Markt =
 * Treuhaender, Pruefer Umwelt/Unfall = Admin. Reine Komfortebene -- die Durchsetzung liegt im
 * Backend. Die Werkszugehoerigkeit des Melders ergibt sich aus der Sichtbarkeit der Meldung
 * (ReportFilterExtension). APPROVED ist hier fuer niemanden bearbeitbar; das Backend antwortet
 * dort mit 409 (D-03).
 *
 * Keine React-/Mantine-Importe -- muss unter `environment: 'node'` ladbar bleiben.
 */
export interface ReportPermissions {
  isReporter: boolean;
  isReviewer: boolean;
  canEdit: boolean;
  canSubmit: boolean;
  canApprove: boolean;
  canReturn: boolean;
}

const NONE: ReportPermissions = {
  isReporter: false,
  isReviewer: false,
  canEdit: false,
  canSubmit: false,
  canApprove: false,
  canReturn: false,
};

export function reportPermissions(
  user: User | null,
  reportType: ReportTypeIdentifier,
  status: ReportStatus
): ReportPermissions {
  const roles = user?.roles ?? [];
  const isMarket = ReportTypeCategory[reportType] === ReportType.MARKET;

  const isReporter = roles.includes('ROLE_PLANT_EMPLOYEE');
  const isReviewer = roles.includes(isMarket ? 'ROLE_TRUSTEE' : 'ROLE_ADMIN');

  if (!isReporter && !isReviewer) {
    return NONE;
  }

  const reporterEditable =
    status === ReportStatus.DRAFT ||
    status === ReportStatus.IN_PROGRESS ||
    status === ReportStatus.REVISION_NEEDED;
  const submitted = status === ReportStatus.SUBMITTED;

  const canEdit = (isReporter && reporterEditable) || (isReviewer && (reporterEditable || submitted));
  // Einreichen: Melder und Pruefer (D-20), nie bei Eingereicht (D-02).
  const canSubmit = reporterEditable && (isReporter || isReviewer);

  return {
    isReporter,
    isReviewer,
    canEdit,
    canSubmit,
    canApprove: isReviewer && submitted,
    canReturn: isReviewer && submitted,
  };
}
